import { cva, VariantProps } from 'class-variance-authority';
import { children, Component, JSX } from 'solid-js';
import { cn } from '~/lib/utils';

const cardMediaVariants = cva(
  'bg-background flex flex-col items-center justify-center [&_img]:w-full [&_img]:h-full [&_img]:object-contain [&_img]:rounded-lg'
);
const cardHeaderVariants = cva('flex flex-col space-y-1.5 pt-4 pb-2 px-4');
const cardTitleVariants = cva('flex items-center gap-2 font-semibold leading-none tracking-tight');
const cardDescriptionVariants = cva('text-sm text-contrast group-hover:text-accent');
const cardContentVariants = cva('px-4 py-0');
const cardFooterVariants = cva('pt-2 px-4 pb-4 flex justify-between items-center', {
  variants: {
    variant: {
      default: '',
      stacked:
        'px-0 pb-0 gap-0 [&_button]:flex-1 [&_button]:rounded-none [&_button]:first:rounded-bl-lg [&_button]:last:rounded-br-lg [&_button]:shadow-none',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const cardVariants = cva(
  'group rounded-lg border border-border bg-card text-card-foreground transition-colors shadow-sm hover:border-border-hover overflow-hidden flex',
  {
    variants: {
      variant: {
        default: '',
        translucide: 'bg-card/40 hover:bg-card/80',
        dark: 'bg-background/40  hover:bg-background/60',
      },
      effect: {
        default: '',
        interactive: 'hover:shadow-md transition-shadow cursor-pointer',
      },
      layout: {
        default: 'flex-col',
        'media-first': 'grid grid-cols-[auto_1fr] gap-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      effect: 'default',
      layout: 'default',
    },
  }
);

export type CardVariants = VariantProps<typeof cardVariants>;
export type CardFooterVariants = VariantProps<typeof cardFooterVariants>;

interface CardProps extends CardVariants {
  class?: string;
  children?: JSX.Element;
  onClick?: (e: MouseEvent) => void;
}

export const Card: Component<CardProps> = (props) => {
  const resolvedChildren = children(() => props.children);
  const kids = resolvedChildren();

  // Si layout est 'media-first', on suppose que le premier enfant est CardMedia
  if (props.layout === 'media-first') {
    let mediaElement: JSX.Element | null = null;
    let otherElements: JSX.Element[] = [];

    if (Array.isArray(kids)) {
      [mediaElement, ...otherElements] = kids;
    } else {
      mediaElement = kids;
    }

    return (
      <div
        class={cn(
          cardVariants({ variant: props.variant, layout: props.layout, effect: props.effect }),
          props.class
        )}
        onClick={props.onClick}
      >
        {mediaElement}
        {otherElements.length > 0 && <div class="flex-1 flex flex-col">{otherElements}</div>}
      </div>
    );
  }

  // Layout par défaut
  return (
    <div
      class={cn(
        cardVariants({ variant: props.variant, layout: props.layout, effect: props.effect }),
        props.class
      )}
      onClick={props.onClick}
    >
      {kids}
    </div>
  );
};

interface CardMediaProps {
  class?: string;
  children?: JSX.Element;
}

export const CardMedia: Component<CardMediaProps> = (props) => (
  <div class={cn(cardMediaVariants(), props.class)}>{props.children}</div>
);

export const CardHeader: Component<{ class?: string; children?: JSX.Element }> = (props) => (
  <div class={cn(cardHeaderVariants(), props.class)}>{props.children}</div>
);

export const CardTitle: Component<{ class?: string; children?: JSX.Element }> = (props) => (
  <h3 class={cn(cardTitleVariants(), props.class)}>{props.children}</h3>
);

export const CardDescription: Component<{ class?: string; children?: JSX.Element }> = (props) => (
  <p class={cn(cardDescriptionVariants(), props.class)}>{props.children}</p>
);

export const CardContent: Component<{ class?: string; children?: JSX.Element }> = (props) => (
  <div class={cn(cardContentVariants(), props.class)}>{props.children}</div>
);

export const CardFooter: Component<
  CardFooterVariants & { class?: string; children?: JSX.Element }
> = (props) => (
  <div class={cn(cardFooterVariants({ variant: props.variant }), props.class)}>
    {props.children}
  </div>
);
